import React, { useState, useEffect, useContext } from 'react';
import { RefreshCw, Key, ShieldCheck, Copy, Plus } from 'lucide-react';
import { SnackbarContext } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../api/api';
import { useConfirm } from '../contexts/ConfirmContext';

export default function VaultWebhooks() {
    const showSnackbar = useContext(SnackbarContext);
    const [secrets, setSecrets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newSecretRaw, setNewSecretRaw] = useState(null); // The 1-time display

    const fetchSecrets = async () => {
        try {
            setLoading(true);
            const res = await api.get(`/api/webhooks`);
            setSecrets(res.data);
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSecrets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleApiError = (error) => {
        if (error.isAppError) {
            showSnackbar(`${error.code || 'Error'}: ${error.message}`, 'error');
        } else {
            showSnackbar('An unexpected error occurred.', 'error');
        }
    };

    const handleCreateWebhook = async () => {
        const displayName = window.prompt("Enter a display name for the Webhook Secret:");
        if (!displayName) return;

        try {
            const res = await api.post(`/api/webhooks`, { displayName });

            // Set 1-time view
            setNewSecretRaw(res.data.plaintextSecret);
            showSnackbar(res.data.message || 'Created successfully', 'success');
            fetchSecrets();
        } catch (error) {
            handleApiError(error);
        }
    };

    const confirm = useConfirm();

    const handleRotate = async (id) => {
        const ok = await confirm("Rotate Webhook Secret?", "Rotating this secret will break existing integrations utilizing it until updated. Approvals mechanism might intercept this request. Proceed?");
        if (!ok) return;

        try {
            const res = await api.post(`/api/webhooks/${id}/rotate`, {});

            if (res.status === 202) {
                showSnackbar(`Rotation queued for Approval`, 'default');
            } else {
                showSnackbar('Secret rotated', 'success');
                // The backend Execution Sandbox returns the new plaintext on execute, not here!
            }
            fetchSecrets();
        } catch (error) {
            handleApiError(error);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        showSnackbar('Copied to clipboard!', 'success');
        setNewSecretRaw(null); // Hide after copy
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Vault & Webhooks</h1>
                    <p className="text-muted-foreground mt-2">
                        Manage Zero-Plaintext external integrations (AES-256-GCM)
                    </p>
                </div>
                <Button onClick={handleCreateWebhook} size="sm">
                    <Plus className="mr-2 h-4 w-4" /> Create Integration
                </Button>
            </div>

            {newSecretRaw && (
                <div className="bg-amber-100 dark:bg-amber-900 border-l-4 border-amber-500 text-amber-900 dark:text-amber-100 p-4 rounded shadow-sm">
                    <div className="flex items-center">
                        <Key className="mr-2 h-5 w-5" />
                        <span className="font-bold">Important: Copy Your Plaintext Secret Now!</span>
                    </div>
                    <p className="mt-2 text-sm">
                        This is the <strong>only time</strong> we will ever show this secret. It is now safely encrypted in the Vault.
                    </p>
                    <div className="mt-4 flex max-w-lg items-center bg-white dark:bg-black rounded border px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-zinc-800"
                        onClick={() => copyToClipboard(newSecretRaw)}>
                        <code className="text-lg flex-1">{newSecretRaw}</code>
                        <Copy className="h-5 w-5 text-muted-foreground" />
                    </div>
                </div>
            )}

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {loading ? (
                    <div className="col-span-full py-4 text-center">Loading Vault...</div>
                ) : secrets.length === 0 ? (
                    <div className="col-span-full py-12 text-center text-muted-foreground border rounded bg-card">
                        <ShieldCheck className="mx-auto h-8 w-8 mb-3 opacity-50" />
                        No webhook secrets generated for this workspace.
                    </div>
                ) : (
                    secrets.map((sec) => (
                        <div key={sec._id} className="rounded-lg border bg-card text-card-foreground shadow-sm p-6 relative">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="font-semibold text-lg">{sec.displayName}</h3>
                                <Badge variant={sec.status === 'ACTIVE' ? 'success' : sec.status === 'ROTATED' ? 'outline' : 'destructive'}>
                                    {sec.status}
                                </Badge>
                            </div>

                            <div className="space-y-4 text-sm text-muted-foreground">
                                <div>
                                    <span className="font-medium text-foreground mr-2">Type:</span>
                                    {sec.secretType}
                                </div>
                                <div>
                                    <span className="font-medium text-foreground block mb-1">Masked Value:</span>
                                    <code className="bg-muted px-2 py-1 rounded text-foreground">{sec.maskedPreview}</code>
                                </div>

                                <div className="pt-4 flex justify-end">
                                    <Button size="sm" variant="outline" onClick={() => handleRotate(sec._id)}
                                        disabled={sec.status !== 'ACTIVE'}>
                                        <RefreshCw className="h-4 w-4 mr-2" /> Request Rotation
                                    </Button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
